import React, { useState, useContext, useEffect } from "react";
import { Card, FormLayout, PageActions, TextContainer, Banner } from "@shopify/polaris";
import PropTypes from "prop-types";
import { useMutation, useQuery } from "@apollo/react-hooks";
import fileDownload from "js-file-download";
import Baby from "babyparse";

// import hoc
import { withErrorBoundary, withFeature } from "lib/hoc";

// import context
import { PrivateContext, OnboardingContext } from "lib/context";

// import constant
import constant from "lib/constant/constant";

// import helper
import { baseHelper, errorHelper } from "lib/helpers";

// import component
import FileUpload from "lib/components/fileInput/fileUpload";
import { Sheet, SkeletonCard } from "lib/components";

// import mutation
import { BULK_UPLOAD } from "app/product/apollo/mutations";

// import query
import { GET_METAFIELD_SETTING } from "app/product/apollo/queries";
import { GET_PRODUCT_LISTING } from "app/productOld/apollo/queries";

// import configuration for downloading csv
import addProductConfig from "app/product/modules/provider/features/add/config/csvData";

const {
  gql: { BULK_PRODUCT_CSV: GQL_BULK_PRODUCT_CSV },
  BULK_PRODUCT_CSV,
} = constant;

const BulkCreate = (props) => {
  const { setBanner } = props;
  const { history, cms } = useContext(PrivateContext) || {};
  const { isOnboarding, onNext, onPrevious } = useContext(OnboardingContext);

  const [values, setValues] = useState({});
  const [metaField, setMetaField] = useState([]);
  const [isSubmitDisabled, setIsSubmitDisabled] = useState(true);
  const [bulkProductUploadRequest, { loading: bulkUploadLoading }] = useMutation(BULK_UPLOAD);
  const [sheetActive, setSheetActive] = useState(false);
  const [isProductList, setIsProductList] = useState(false);

  // const [sheetTitle, setSheetTitle] = useState("");
  // const [sheetContent, setSheetContent] = useState("");

  const { loading: metaFieldLoading, data: metaFieldData } = useQuery(GET_METAFIELD_SETTING);
  const { loading: loadingProductListing, data: dataProductList } = useQuery(GET_PRODUCT_LISTING);

  useEffect(() => {
    if (metaFieldData) {
      const metaFieldSetting = baseHelper.getResponseData(metaFieldData, constant.gql.GET_METAFIELD_SETTING) || {};
      const { metafield = [] } = metaFieldSetting;

      if (metafield && metafield.length) {
        setMetaField(metafield);
      }
    }
  }, [metaFieldData]);

  useEffect(() => {
    if (dataProductList) {
      const productistResponse = baseHelper.getResponseData(dataProductList, "getProductListingSetting");
      if (productistResponse) {
        const { isEnabled = {} } = productistResponse;
        if (isEnabled) {
          setIsProductList(true);
        }
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [dataProductList]);

  const getCSVData = (csvValue) => {
    const regexForEnterKey = /\n,|\n/gi;
    const csvDataAfterRegex = csvValue.data.replace(regexForEnterKey, "\n");
    return csvDataAfterRegex;
  };

  const onSubmit = async () => {
    if (!values.data) {
      setBanner({
        isOpen: true,
        title: cms("common.message.error.csvIsRequired"),
        status: constant.CRITICAL,
      });
      return;
    }
    const modifiedFormat = getCSVData(values);
    const requestData = {
      file: {
        file: modifiedFormat,
        fileName: values.fileName,
      },
    };
    try {
      const res = await bulkProductUploadRequest({
        variables: {
          input: requestData,
        },
      });

      const resData = baseHelper.getResponseData(res.data, GQL_BULK_PRODUCT_CSV);
      let banner = {
        isOpen: true,
        status: "info",
        title: cms("section.bulk.message.success.title"),
        children: cms("section.bulk.message.success.content"),
        action: {
          id: "viewLogs",
          content: cms("section.bulk.message.success.action"),
          onAction: () => history.push("/activity/bulk"),
        },
      };

      if (!resData) {
        const error = baseHelper.getResponseError(res.data, GQL_BULK_PRODUCT_CSV);
        banner = {
          action: null,
          isOpen: true,
          status: constant.CRITICAL,
          title: error,
        };
      }
      setIsSubmitDisabled(true);
      setBanner(banner);

      if (isOnboarding && resData) {
        onNext();
      }
    } catch (exception) {
      setBanner({
        isOpen: true,
        status: constant.CRITICAL,
        title: errorHelper.parse(exception),
      });
    }
  };
  const primaryAction = {
    content: cms("common.label.done"),
    onAction: () => setSheetActive(false),
  };

  const secondaryAction = {
    content: cms("common.button.cancel"),
    onAction: () => setSheetActive(false),
  };
  // const learnMore = (title, content) => {
  //   setSheetActive(true);
  //   setSheetTitle(title);
  //   setSheetContent(content);
  // };

  const downloadCSV = () => {
    const keysToAdd = (metaField || []).map((field) => field.key).filter((item) => item);
    addProductConfig.forEach((csvData) => {
      keysToAdd.forEach((key) => {
        // eslint-disable-next-line no-param-reassign
        csvData[key] = "";
      });
    });
    const keyToAddListValue = isProductList ? ["Listing Quantity", "Campaign"] : [];
    addProductConfig.forEach((csvData) => {
      keyToAddListValue.forEach((key) => {
        // eslint-disable-next-line no-param-reassign
        csvData[key] = "";
      });
    });
    const csv = Baby.unparse(addProductConfig);
    fileDownload(csv, BULK_PRODUCT_CSV);
  };

  useEffect(() => {
    setIsSubmitDisabled(!values.data);
  }, [values]);

  if (metaFieldLoading) {
    return <SkeletonCard />;
  }

  return (
    <>
      <Card
        title={cms("section.bulk.title")}
        // actions={[
        //   {
        //     content: cms("common.label.learnMore"),
        //     onAction: () => learnMore(cms("section.bulk.title"), "TODO:"),
        //   },
        // ]}
      >
        <Card.Section>
          <TextContainer>
            {cms("section.bulk.description")}
            <br />
            <Banner 
              title={cms("section.bulk.label.sampleCsv")}
              status="info"
            >
              <p>
                Handle, Title, Vendor, Option1 Name, Option1 Value, and Variant Price are required columns.
                All other column headers must be present in the CSV but their values can be left empty.
              </p>
            </Banner>
          </TextContainer>
          <br />
          <FormLayout>
            <FileUpload setValue={setValues} getCSV={downloadCSV} values={values} />
          </FormLayout>
          <PageActions
            primaryAction={{
              content: cms("section.bulk.button.primary"),
              id: "submitButton",
              onAction: () => onSubmit(),
              loading: bulkUploadLoading,
              disabled: bulkUploadLoading || !Object.values(values).length || isSubmitDisabled,
            }}
            secondaryActions={[
              {
                content: cms("common.button.cancel"),
                id: "cancelButton",
                onAction: () => (isOnboarding && onPrevious()) || history.push("/products"),
              },
            ]}
          />
        </Card.Section>
      </Card>
      <Sheet
        // title={sheetTitle}
        isOpen={sheetActive}
        onClose={() => setSheetActive(false)}
        primaryAction={primaryAction}
        secondaryAction={secondaryAction}
      >
        {/* {sheetContent} */}
      </Sheet>
    </>
  );
};

BulkCreate.propTypes = {
  setBanner: PropTypes.func.isRequired,
};

export default withFeature(withErrorBoundary(BulkCreate), {
  feature: constant.PRODUCT_ADD_CSV,
  // subFeature: constant.PRODUCT_ADD_CSV,
});
